import { useEffect, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import config from "@/v2-status-client/config"
import { formatUpdatedAgo } from "@/v2-status-client/status/status.utils"
import StatusFooterLink from "@/v2-status-client/status/footer/StatusFooterLink"

export type StatusFooterProps = {
  error: string | null
  // True while a poll is in flight. Drives the pulsing pip and a
  // "Checking for updates…" copy so the customer knows the page
  // is doing something even when there's nothing on screen yet.
  isRefreshing: boolean
  lastSuccessAt: Date | null
}

// Bottom strip: polling pip + "updated 12 s ago" copy on top, brand
// nav (placepoint.no / Console / Docs / RSS) underneath. Owns the
// per-second tick locally so the rest of the page only re-renders
// on a real data change (~1/min) -- the previous implementation
// hoisted the ticker to StatusPage and re-rendered the entire tree
// every second just to update this single span.
//
// The pulse animation lives in `styles.css` because Tailwind doesn't
// ship the keyframes we want; everything else is utility classes.
function StatusFooter(props: Readonly<StatusFooterProps>) {
  const { error, isRefreshing, lastSuccessAt } = props
  const now = useNowTicker()
  return (
    <footer className="flex justify-between gap-3 border-t border-border/60 pt-4 text-xs text-muted-foreground">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            "status-footer-pip inline-block size-2 rounded-full",
            error ? "bg-amber-400" : "bg-emerald-400",
          )}
          aria-hidden
        />
        <span className="flex-1 min-w-0">
          {renderFooterCopy({ error, isRefreshing, lastSuccessAt, now })}
        </span>
      </div>

      <nav
        aria-label="Placepoint"
        className="flex flex-wrap items-center gap-x-5 gap-y-1"
      >
        <StatusFooterLink
          href={config.marketingUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Placepoint
        </StatusFooterLink>

        <StatusFooterLink
          href={config.consoleV1Url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Console v1
        </StatusFooterLink>

        <StatusFooterLink
          href={config.consoleV2Url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Console v2
        </StatusFooterLink>

        <StatusFooterLink
          href={config.propcloudUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Propcloud
        </StatusFooterLink>

        <StatusFooterLink
          href={config.newsecmapsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Newsecmaps
        </StatusFooterLink>

        <StatusFooterLink
          href={config.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Docs
        </StatusFooterLink>

        <StatusFooterLink
          href="/public/incidents.atom"
          rel="alternate"
          type="application/atom+xml"
        >
          Subscribe via RSS
        </StatusFooterLink>
      </nav>
    </footer>
  )
}

// Pip copy is forked off the four states the polling lifecycle
// can land in: still loading (no success yet, no error), erroring
// (could be with or without a previous success), and the steady
// state ("updated N s ago"). Done as a free function so the JSX
// above stays readable.
function renderFooterCopy(args: {
  error: string | null
  isRefreshing: boolean
  lastSuccessAt: Date | null
  now: Date
}): string {
  const { error, isRefreshing, lastSuccessAt, now } = args
  if (error) return `Last update failed (${error}). Retrying…`
  if (!lastSuccessAt) {
    return isRefreshing ? "Checking for updates…" : "Waiting for status…"
  }
  return `Updated ${formatUpdatedAgo(lastSuccessAt, now)}`
}

// Ticks once a second so the "updated 12 s ago" footer counter
// counts up between polls. Scoped to this file because the footer
// is the only consumer; lifting it any higher would force the
// whole page to re-render every second.
function useNowTicker(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  return now
}

export default StatusFooter
